import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getBackendUrl } from '../utils/apiUrl';
import { clearSessionToken, getSessionToken } from '../utils/secureStorage';
import { createTimeoutSignal, authCircuitBreaker, isPreviewEnvironment } from './authHelpers';
import { useOAuthHandler } from './useOAuthHandler';
import { useSessionLifecycle } from './useSessionLifecycle';
import { fetchWithRetry } from '../utils/fetchWithRetry';

import devLog from '../utils/devLog';

/**
 * V37.0: AuthContext slimmed down - OAuth handling and session lifecycle
 * extracted to useOAuthHandler.js and useSessionLifecycle.js
 */

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [showSessionBanner, setShowSessionBanner] = useState(false); // P1 FIX: Session health UI
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false); // FIX: Prevent zero-credit flash during OAuth
  
  // CRITICAL FIX: Use runtime URL resolution to avoid internal hostname injection
  const BACKEND_URL = getBackendUrl();
  
  // Check if this is a preview environment
  const isPreview = isPreviewEnvironment();
  
  // Refs for cleanup
  const networkRetryRef = useRef(null);
  const userRef = useRef(user); // P1 FIX: Ref to break dependency cycle
  
  // Update user ref
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  // LOCKED: Session stability tracking - NEVER auto-logout on network errors
  const consecutiveFailures = useRef(0);
  const maxRetries = 10; // DOUBLED from 5 for maximum stability
  const retryDelay = 2000; // 2 second delay between retries
  
  // Performance optimization: Track if auth is stable
  const [isSessionStable, setIsSessionStable] = useState(false);

  // Enhanced session ID processing with RETRY LOGIC - P1 FIX
  const processSessionId = useCallback(async (sessionId, retryCount = 0) => {
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
    
    devLog.log(`[Auth] ⚡ Processing session (attempt ${retryCount + 1}/${maxRetries + 1}):`, sessionId.substring(0, 10) + '...');
    setLoading(true);
    
    // V32.0.4: Clean URL but PRESERVE auth state in history to prevent race condition
    if (retryCount === 0) {
      window.history.replaceState({ auth: 'processing', session: sessionId }, '', window.location.pathname);
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
        signal: createTimeoutSignal(10000) // V25.3.4: Safari-compatible timeout
      });

      if (response.ok) {
        const data = await response.json();
        
        // Set user data IMMEDIATELY
        setUser(data.user);
        setCredits(data.user.credits);
        setIsSessionStable(true);
        consecutiveFailures.current = 0;
        setLoading(false);
        
        // V32.0.4: Mark auth as 'authenticated' in history state to prevent logout
        window.history.replaceState({ auth: 'authenticated' }, '', window.location.pathname);
        
        // P1 FIX: Store session backup in localStorage
        try {
          sessionStorage.setItem('ep_session_backup', JSON.stringify({
            user: data.user,
            timestamp: Date.now()
          }));
        } catch (e) {
          devLog.warn('[Auth] Failed to store session backup:', e);
        }

        // V37.43.1 — Broadcast login to sibling tabs so they re-hydrate
        // their React state without a manual refresh. Only fired on a
        // genuine OAuth session-id exchange; existing-session refreshes
        // don't need this because siblings already have their own state.
        try {
          localStorage.setItem('ep_login_signal', String(Date.now()));
          localStorage.removeItem('ep_login_signal');
        } catch (_e) { /* ignore storage quota */ }

        devLog.log('✅ [Auth] ⚡ Login success for:', data.user.email);
        return true;
      } else if (response.status === 401 || response.status === 403) {
        // Auth error - don't retry
        console.error('[Auth] Authentication failed:', response.status);
        setUser(null);
        setCredits(null);
        setLoading(false);
        return false;
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error(`[Auth] Session processing error (attempt ${retryCount + 1}):`, error.message);
      
      // P1 FIX: Retry logic with exponential backoff for network errors
      if (retryCount < maxRetries && (error.name === 'AbortError' || error.name === 'TypeError' || error.message.includes('fetch'))) {
        const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s, 4s
        devLog.log(`🔄 [Auth] Retrying session processing in ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return processSessionId(sessionId, retryCount + 1);
      }
      
      // P1 FIX: After exhausting retries, try to recover from localStorage
      devLog.warn('[Auth] All retries exhausted, attempting localStorage recovery...');
      try {
        const backup = sessionStorage.getItem('ep_session_backup');
        if (backup) {
          const { user: backupUser, timestamp } = JSON.parse(backup);
          const age = Date.now() - timestamp;
          
          // Use backup if less than 24 hours old
          if (age < 24 * 60 * 60 * 1000) {
            devLog.log('✅ [Auth] Recovered session from localStorage backup');
            setUser(backupUser);
            setCredits(backupUser.credits);
            setIsSessionStable(false); // Mark as unstable - needs refresh
            setLoading(false);
            return true;
          }
        }
      } catch (e) {
        devLog.warn('[Auth] Failed to recover from backup:', e);
      }
      
      // Final fallback - clear session
      setUser(null);
      setCredits(null);
      setLoading(false);
      return false;
    }
  }, [BACKEND_URL]);

  // Robust session checking with retry logic
  // Robust session checking with retry logic.
  // V37.43.3 — `crossTabSignal` bypasses the preview-env skip ONLY to allow
  // a call to the real /api/auth/me endpoint. Auth state still derives
  // exclusively from the backend 200-response user data; we never fake
  // history state or set user locally. If /api/auth/me fails, the tab
  // stays unauthenticated — same behavior as any other failed check.
  const checkExistingSession = useCallback(async (retryCount = 0, crossTabSignal = false) => {
    devLog.log(`[Auth] Checking for existing session (attempt ${retryCount + 1})...`);
    devLog.log(`[Auth] Preview environment: ${isPreview}`);
    
    // V24.71 SAFARI FIX: Check history state for OAuth in progress
    // This prevents the race condition where URL is checked after being cleaned
    // V24.99 FIX: Also check for 'processed' to prevent logout after successful auth
    // V32.0.4 FIX: Also check for 'authenticated' - set after successful login
    const historyState = window.history.state;
    const isOAuthProcessing = historyState?.auth === 'processing' || historyState?.auth === 'processed' || historyState?.auth === 'authenticated';
    
    // Also check URL for OAuth callback (fallback for browsers that don't support history state)
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuthCallbackInUrl = urlParams.get('auth') === 'success';
    const isOAuthCallback = isOAuthProcessing || isOAuthCallbackInUrl;
    
    devLog.log(`[Auth] OAuth callback check: historyState=${isOAuthProcessing}, urlParams=${isOAuthCallbackInUrl}`);
    
    // PREVIEW ENVIRONMENT FIX: Don't auto-restore sessions from localStorage
    // User must explicitly sign in to preview environments.
    // V37.43.3: A cross-tab login signal IS an explicit user sign-in in a
    // sibling tab, so the preview skip is bypassed. Auth is still verified
    // against the real /api/auth/me response below — no simulated state.
    if (isPreview && !crossTabSignal) {
      devLog.log('[Auth] 🔒 Preview environment - checking for explicit OAuth callback only');
      
      if (!isOAuthCallback) {
        // No OAuth callback - don't try to restore session in preview
        devLog.log('[Auth] 🔒 Preview environment - no OAuth callback, staying signed out');
        setUser(null);
        setCredits(null);
        setLoading(false);
        
        // P5: Clear in-memory token in preview to prevent auto-login
        clearSessionToken();
        
        return false;
      }
      // If it IS an OAuth callback, continue with normal flow to process it
      devLog.log('[Auth] 🔒 Preview environment - OAuth callback detected, proceeding with session check');
    }
    
    // P5 SECURITY: Use in-memory ref only (httpOnly cookie is primary via credentials: 'include')
    const sessionToken = getSessionToken();
    
    try {
      // Build headers — include in-memory token as Authorization header for Safari fallback
      const headers = {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      };
      
      // Safari fallback: send token via Authorization header if available in memory
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        devLog.log('[Auth] Sending session via Authorization header (Safari fallback)');
      }
      
      const response = await fetchWithRetry(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setCredits(userData.credits);
        setIsSessionStable(true);
        consecutiveFailures.current = 0;
        
        // V21.5: Store user email in localStorage for admin dashboard access
        if (userData.email) {
          try {
            sessionStorage.setItem('ep_user_email', userData.email);
            devLog.log('[Auth] Stored user email in localStorage:', userData.email);
          } catch (e) {
            devLog.warn('[Auth] Failed to store email in localStorage:', e);
          }
        }
        
        devLog.log('✅ [Auth] Session restored successfully:', userData.email);
        return true;
      } else if (response.status === 401) {
        devLog.log('ℹ️ [Auth] No existing session found (401)');
        setUser(null);
        setCredits(null);
        return false;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`❌ [Auth] Session check error (attempt ${retryCount + 1}):`, error.message);
      
      // Retry logic for network errors
      if (retryCount < 2 && (error.name === 'NetworkError' || error.name === 'TypeError')) {
        devLog.log(`🔄 [Auth] Retrying session check in ${retryDelay}ms...`);
        return new Promise(resolve => {
          networkRetryRef.current = setTimeout(() => {
            checkExistingSession(retryCount + 1).then(resolve);
          }, retryDelay);
        });
      }
      
      // P1 FIX: After exhausting retries, try localStorage recovery first
      if (userRef.current) {
        devLog.warn('⚠️ [Auth] Network error but keeping existing session');
        return true; // Keep existing session
      }
      
      // Try to recover from localStorage backup before giving up
      try {
        const backup = sessionStorage.getItem('ep_session_backup');
        if (backup) {
          const { user: backupUser, timestamp } = JSON.parse(backup);
          const age = Date.now() - timestamp;
          
          if (age < 24 * 60 * 60 * 1000) {
            devLog.log('✅ [Auth] Recovered initial session from localStorage backup');
            setUser(backupUser);
            setCredits(backupUser.credits);
            setIsSessionStable(false); // Needs verification
            return true;
          } else {
            devLog.log('[Auth] localStorage backup too old, clearing');
            sessionStorage.removeItem('ep_session_backup');
          }
        }
      } catch (e) {
        devLog.warn('[Auth] Failed to recover from localStorage:', e);
      }
      
      setUser(null);
      setCredits(null);
      return false;
    } finally {
      if (retryCount === 0) { // Only set loading false on initial check
        setLoading(false);
        devLog.log('[Auth] Initial session check complete');
      }
    }
  }, [BACKEND_URL, isPreview]);
  // V37.0: OAuth callback handling extracted to useOAuthHandler
  useOAuthHandler({ checkExistingSession, setIsProcessingOAuth, setLoading });

  // Enhanced token refresh with better error handling
  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        devLog.log('✅ [Auth] Session refreshed successfully, expires:', data.expires_at);
        consecutiveFailures.current = 0;
        setIsSessionStable(true);
        return true;
      } else if (response.status === 401) {
        devLog.warn('⚠️ [Auth] Session refresh failed - session expired');
        return false;
      } else {
        devLog.warn('⚠️ [Auth] Session refresh failed with status:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[Auth] Session refresh error:', error);
      return false;
    }
  }, [BACKEND_URL]);

  // P1 FIX: Circuit breaker for silent refresh failures
  const silentRefreshFailures = useRef(0);
  const lastSilentRefreshAttempt = useRef(0);
  const CIRCUIT_BREAKER_THRESHOLD = 5;
  const CIRCUIT_BREAKER_COOLDOWN = 5 * 60 * 1000; // 5 minutes

  // LOCKED: Ultra-stable user data refresh - NEVER auto-logout on network errors
  const refreshUser = useCallback(async (silent = false) => {
    // V25.3.4: Safari rate limit protection - check circuit breaker first
    if (!authCircuitBreaker.canAttempt()) {
      devLog.log('[Auth V25.3.4] ⏳ Circuit breaker blocking refresh - rate limited');
      return false;
    }
    
    // P1 FIX: Circuit breaker - don't spam failed silent refreshes
    if (silent) {
      const now = Date.now();
      const timeSinceLastAttempt = now - lastSilentRefreshAttempt.current;
      
      if (silentRefreshFailures.current >= CIRCUIT_BREAKER_THRESHOLD) {
        if (timeSinceLastAttempt < CIRCUIT_BREAKER_COOLDOWN) {
          devLog.log(`[Auth] 🚫 Circuit breaker active - skipping silent refresh (${Math.round((CIRCUIT_BREAKER_COOLDOWN - timeSinceLastAttempt) / 1000)}s cooldown remaining)`);
          return false;
        } else {
          // Reset circuit breaker after cooldown
          devLog.log('[Auth] ✅ Circuit breaker reset - attempting silent refresh');
          silentRefreshFailures.current = 0;
        }
      }
      
      lastSilentRefreshAttempt.current = now;
    }

    // Skip if we're in an unstable state and this is a background call
    if (!silent && !isSessionStable) {
      devLog.log('[Auth] Skipping user refresh - session not stable');
      return;
    }

    try {
      const response = await fetchWithRetry(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        // V25.3.4: Safari-compatible timeout signal
        signal: createTimeoutSignal(10000)
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setCredits(userData.credits);
        // CRITICAL: Reset failure counter on success
        consecutiveFailures.current = 0;
        setIsSessionStable(true);
        
        // V25.3.4: Reset circuit breaker on success
        authCircuitBreaker.recordSuccess();
        
        // P1 FIX: Reset silent refresh failure counter on success
        if (silent) {
          silentRefreshFailures.current = 0;
        }
        
        // V21.5: Store user email in localStorage for admin dashboard access
        if (userData.email) {
          try {
            sessionStorage.setItem('ep_user_email', userData.email);
          } catch (e) {
            devLog.warn('[Auth] Failed to store email:', e);
          }
        }
        
        // P1 FIX: Update localStorage backup on successful refresh
        try {
          sessionStorage.setItem('ep_session_backup', JSON.stringify({
            user: userData,
            email: userData.email, // V21.5: Include email in backup
            timestamp: Date.now()
          }));
        } catch (e) {
          devLog.warn('[Auth] Failed to update session backup:', e);
        }
        
        if (!silent) {
          devLog.log('✅ [Auth] User data refreshed successfully');
        }
        return true;
      } else if (response.status === 401) {
        // LOCKED FIX: Increment failure counter ONLY for 401
        consecutiveFailures.current += 1;
        
        // CRITICAL: NEVER auto-logout - require manual user action
        // Background calls should NEVER trigger logout
        devLog.warn(`⚠️ [Auth] Got 401 (${consecutiveFailures.current} consecutive), but KEEPING user logged in (LOCKED BEHAVIOR)`);
        
        // P1 FIX: Mark session as unstable AND show UI banner
        if (consecutiveFailures.current >= maxRetries * 2) { // Double the threshold
          setIsSessionStable(false);
          setShowSessionBanner(true); // Show banner to user
          devLog.warn('⚠️ [Auth] Session marked as unstable - showing UI banner');
        }
        return false;
      } else if (response.status === 429) {
        // V25.3.4: Rate limited - trigger circuit breaker
        devLog.warn('[Auth V25.3.4] ⚠️ Rate limited (429) - activating circuit breaker');
        authCircuitBreaker.recordFailure();
        return false;
      } else {
        // Non-401 errors (500, 503, etc.) should NOT count as auth failures
        devLog.warn(`⚠️ [Auth] Server error ${response.status} - NOT counting as auth failure`);
        return false;
      }
    } catch (error) {
      // LOCKED FIX: Network errors (sleep/wake, timeout) should NEVER log user out
      console.error('[Auth] User refresh error (network issue, NOT logging out):', error.name, error.message);
      
      // V25.3.4: Record failure for circuit breaker
      authCircuitBreaker.recordFailure();
      
      // P1 FIX: Track silent refresh failures for circuit breaker
      if (silent) {
        silentRefreshFailures.current += 1;
        devLog.log(`⚠️ [Auth] Silent refresh failure count: ${silentRefreshFailures.current}/${CIRCUIT_BREAKER_THRESHOLD}`);
      }
      
      // Don't increment consecutive failures for network errors
      // These are often temporary (sleep/wake cycles)
      if (error.name === 'AbortError' || error.name === 'NetworkError' || error.name === 'TypeError') {
        devLog.log('🌐 [Auth] Network error detected - likely sleep/wake or temporary issue. Keeping session.');
      }
      
      // Keep session stable unless explicitly told otherwise
      return false;
    }
  }, [BACKEND_URL, isSessionStable, maxRetries, CIRCUIT_BREAKER_COOLDOWN]);

  // V37.0: Session lifecycle (refresh, polling, sleep/wake, activity) extracted to useSessionLifecycle
  // IMPORTANT: Must be called AFTER refreshToken and refreshUser are defined
  useSessionLifecycle({
    user,
    isSessionStable,
    refreshToken,
    refreshUser,
    setUser,
    setCredits,
    setIsSessionStable,
  });

  // Google OAuth login - Uses native Google Sign-In
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = useCallback(() => {
    devLog.log('[Auth] Initiating Google OAuth login flow...');
    setLoading(true);
    
    // V24.90.3 SAFARI FIX: Clear ALL existing auth state before starting OAuth
    // Safari ITP can leave stale state that blocks new OAuth flows
    const safariClearAuthState = () => {
      devLog.log('[Auth] Clearing auth state...');
      
      // P5: Clear in-memory token
      clearSessionToken();
      
      // Clear non-token storage items
      try {
        localStorage.removeItem('ep_auth_in_progress');
        sessionStorage.removeItem('ep_auth_in_progress');
      } catch (e) {
        devLog.warn('[Auth] Storage clear failed:', e);
      }
      
      // Clear all cookies
      try {
        const cookieNames = ['session_token', 'auth_token', 'oauth_session', 'ep_session'];
        const domains = ['', '.elonprompt.ai', window.location.hostname];
        cookieNames.forEach(name => {
          domains.forEach(domain => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ''}`;
          });
        });
        devLog.log('[Auth] ✅ Cookies cleared');
      } catch (e) {
        devLog.warn('[Auth] Cookie clear failed:', e);
      }
      
      // Clear IndexedDB
      try {
        if (window.indexedDB) {
          indexedDB.deleteDatabase('ElonPromptAuth');
          devLog.log('[Auth] ✅ IndexedDB cleared');
        }
      } catch (e) {
        devLog.warn('[Auth] IndexedDB clear failed:', e);
      }
    };
    
    // Always clear before OAuth (especially important for Safari)
    safariClearAuthState();
    
    // V24.90.3: Mark that OAuth is in progress (for Safari to detect on return)
    try {
      localStorage.setItem('ep_auth_in_progress', Date.now().toString());
      sessionStorage.setItem('ep_auth_in_progress', Date.now().toString());
    } catch (e) {
      devLog.warn('[Auth] Could not set auth_in_progress flag:', e);
    }
    
    // CRITICAL FIX: Use window.location.origin to ensure OAuth uses the PUBLIC domain
    // This prevents redirect_uri_mismatch when behind a reverse proxy/load balancer
    // The backend API is served from the same domain via Kubernetes ingress routing
    const authUrl = `${window.location.origin}/api/auth/google`;
    
    // Add performance hints for faster redirect
    if (window.performance && window.performance.mark) {
      window.performance.mark('auth-redirect-start');
    }
    
    devLog.log('[Auth] Redirecting to Google OAuth:', authUrl);
    
    // V24.90.3 SAFARI FIX: Use direct navigation instead of assignment
    // Safari sometimes has issues with window.location.href assignment in certain contexts
    window.location.replace(authUrl);
  }, []);

  // Clean logout function - PHASE 5: Enhanced to handle cross-domain sessions
  const performLogout = useCallback(async (redirect = true) => {
    devLog.log('[Auth] Performing logout...');
    
    try {
      // Clear network retry timer
      if (networkRetryRef.current) {
        clearTimeout(networkRetryRef.current);
        networkRetryRef.current = null;
      }
      
      // Clear state IMMEDIATELY
      setUser(null);
      setCredits(null);
      setIsSessionStable(false);
      consecutiveFailures.current = 0;
      
      // P5: Clear in-memory token and non-sensitive storage
      clearSessionToken();
      localStorage.removeItem('user');
      localStorage.removeItem('auth_state');
      sessionStorage.removeItem('ep_session_backup');

      // V37.43.0 — Broadcast logout to other tabs via localStorage `storage`
      // event. setItem → removeItem ensures both events fire reliably.
      try {
        localStorage.setItem('ep_logout_signal', String(Date.now()));
        localStorage.removeItem('ep_logout_signal');
      } catch (_e) { /* ignore storage quota */ }
      
      // Call backend logout (best effort)
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (error) {
        devLog.warn('[Auth] Backend logout failed (non-critical):', error);
      }
      
      // PHASE 5: Force clear cookies on all possible domains
      const domains = ['', '.elonprompt.ai', 'app.elonprompt.ai', '.emergentagent.com', '.emergent.host', window.location.hostname];
      const cookieNames = ['session_token', 'auth_token', 'user_session', 'oauth_session'];
      
      domains.forEach(domain => {
        cookieNames.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ''}`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;${domain ? ` domain=${domain};` : ''}`;
        });
      });
      
      devLog.log('[Auth] Logout complete - cleared all sessions and cookies');
      
      if (redirect) {
        // Force hard redirect to clear any cached state
        window.location.replace('/');
      }
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      // Still force redirect on error to ensure logout
      if (redirect) {
        window.location.replace('/');
      }
    }
  }, [BACKEND_URL]);

  // Public logout function
  const logout = useCallback(() => performLogout(true), [performLogout]);

  // V37.43.0 — Cross-tab session sync. Leverages the native `storage` event
  // to broadcast logout instantly to all open tabs (same origin) without
  // WebSockets or polling. `storage` events fire only in OTHER tabs, never
  // the originating one, so this is naturally one-directional.
  //
  // Flow:
  //   • LOGOUT: performLogout() writes `ep_logout_signal` then removes it.
  //     Sibling tabs run their own performLogout(true).
  //   • V37.43.1 LOGIN: successful auth writes `ep_login_signal` with a
  //     fresh timestamp. Sibling tabs that are NOT authenticated fetch
  //     /api/auth/me (the session cookie is already set by the backend, so
  //     this just re-hydrates local React state) — no page refresh.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'ep_logout_signal') {
        if (!userRef.current) return;
        devLog.log('[Auth V37.43.0] Cross-tab logout signal received — logging out');
        performLogout(true);
        return;
      }
      if (e.key === 'ep_login_signal' && e.newValue) {
        // Only re-check if this tab is currently unauthenticated.
        if (userRef.current) return;
        devLog.log('[Auth V37.43.3] Cross-tab login signal received — revalidating session via backend');
        // V37.43.3 — Real-session-only hydration. No history.replaceState,
        // no client-side auth simulation. checkExistingSession will call
        // /api/auth/me; state populates ONLY if the backend confirms 200.
        checkExistingSession(0, true).catch(() => { /* already logged */ });
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [performLogout, checkExistingSession]);

  // P1 FIX: Manual session refresh function for UI banner
  const manualRefreshSession = useCallback(async () => {
    devLog.log('[Auth] Manual session refresh triggered by user');
    setShowSessionBanner(false);
    consecutiveFailures.current = 0;
    
    const success = await refreshUser(false);
    if (success) {
      setIsSessionStable(true);
      devLog.log('✅ [Auth] Manual refresh successful');
    } else {
      devLog.warn('⚠️ [Auth] Manual refresh failed');
      setShowSessionBanner(true);
    }
    return success;
  }, [refreshUser]);

  const value = {
    user,
    loading,
    credits,
    login,
    logout,
    refreshUser,
    refreshToken,
    isAuthenticated: !!user,
    isSessionStable,
    isProcessingOAuth, // FIX: Expose OAuth processing state to prevent zero-credit flash
    showSessionBanner, // P1 FIX: Expose banner state
    dismissSessionBanner: () => setShowSessionBanner(false), // P1 FIX: Allow dismissing
    manualRefreshSession, // P1 FIX: Manual refresh function
    refreshCredits: () => refreshUser(true), // Manual credit refresh function
    setCreditsImmediate: (newBalance) => {
      const bal = Number(newBalance);
      if (!isNaN(bal)) {
        setCredits(bal);
        // V37.41.1 — must update `user` via setUser so React re-renders
        // any consumer reading `user.credits` (e.g., Header credit badge).
        // Direct mutation (user.credits = bal) was insufficient because
        // React doesn't track in-place mutation; the Header only updated
        // when refreshUser eventually called setUser, causing the visible
        // credit-deduction lag after final render.
        setUser((prev) => (prev ? { ...prev, credits: bal } : prev));
      }
    },
    // New functions for better control
    checkSession: () => checkExistingSession(),
    getCredits: () => credits,
    getUserData: () => user,
    // Preview environment state
    isPreviewEnvironment: isPreview
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
